import { createSlice } from '@reduxjs/toolkit';

/**
 * Slack integration slice (Slack integration Phase 1 — org-channel alerts). Holds the org's Slack
 * connection status + its configured alert-destination channels, and the live channel picker list
 * fetched from Slack on demand. Keyed by orgID. Saga-backed (SuperFetch to /slack); org-wide-admin
 * + `slackIntegration` are server-enforced, the UI just gates visibility.
 *
 *   connection: { connected, workspace, channels }  — the saved connection + destination channels
 *   available:  Slack's live channel list (for the "add a channel" picker; fetched on open)
 */
const initialState = { byOrg: {}, error: null };

const ensure = (state, orgID) => {
	if (!state.byOrg[orgID])
		state.byOrg[orgID] = {
			connected: false,
			workspace: null,
			channels: [], // saved alert-destination channels
			available: [], // live Slack channel list for the picker
			loading: false,
			loaded: false,
			loadingAvailable: false,
			saving: false
		};
	return state.byOrg[orgID];
};

const slackSlice = createSlice({
	name: 'slack',
	initialState,
	reducers: {
		loadConnection: () => {}, // trigger { orgID }
		loadConnectionStart(state, action) {
			ensure(state, action.payload.orgID).loading = true;
			state.error = null;
		},
		loadConnectionSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.connected = !!action.payload.connected;
			s.workspace = action.payload.workspace || null;
			s.channels = action.payload.channels || [];
			s.loading = false;
			s.loaded = true;
		},
		loadConnectionError(state, action) {
			ensure(state, action.payload.orgID).loading = false;
			state.error = action.payload.error;
		},

		// Live Slack channel picker list.
		loadAvailable(state, action) {
			ensure(state, action.payload.orgID).loadingAvailable = true;
		},
		loadAvailableSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.available = action.payload.channels || [];
			s.loadingAvailable = false;
		},
		loadAvailableError(state, action) {
			ensure(state, action.payload.orgID).loadingAvailable = false;
		},

		addChannel(state, action) {
			ensure(state, action.payload.orgID).saving = true;
		},
		addChannelSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.saving = false;
			const ch = action.payload.channel;
			if (ch && !s.channels.some((c) => c.slackChannelID === ch.slackChannelID)) s.channels.push(ch);
		},
		addChannelError(state, action) {
			ensure(state, action.payload.orgID).saving = false;
		},

		removeChannel(state, action) {
			ensure(state, action.payload.orgID).saving = true;
		},
		removeChannelSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.saving = false;
			s.channels = s.channels.filter((c) => c.slackChannelID !== action.payload.slackChannelID);
		},
		removeChannelError(state, action) {
			ensure(state, action.payload.orgID).saving = false;
		},

		disconnect(state, action) {
			ensure(state, action.payload.orgID).saving = true;
		},
		disconnectSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.saving = false;
			s.connected = false;
			s.workspace = null;
			s.channels = [];
			s.available = [];
		},
		disconnectError(state, action) {
			ensure(state, action.payload.orgID).saving = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const slackActions = slackSlice.actions;
export default slackSlice.reducer;
